// Runtime config, mirrors landing-v2's window.OnAim[<repo>] pattern.
// A one-time-built embed.js gets loaded cross-origin by arbitrary host pages,
// so config can't be baked in at build time (unlike a normal Vite app) —
// instead we self-inject env.js from *our own* origin and read the values it
// writes onto window.OnAim.

export type Environment = {
  GATEWAY_BASE_URL: string
  WIDGET_SERVICE_BASE_URL: string
}

declare global {
  interface Window {
    OnAim?: Record<string, unknown> & { 'minimal-widget'?: Environment }
  }
}

// document.currentScript works for a classic <script src="embed.js"> (doc's
// Method 1), but is always null for <script type="module"> (Method 2) — so it
// can't be the only detection path. Scan for our own script tag by src
// instead; that works regardless of how the host page loaded us.
function detectOrigin(): string {
  if (typeof document !== 'undefined') {
    const current = document.currentScript as HTMLScriptElement | null
    if (current?.src) {
      try {
        return new URL(current.src).origin
      } catch {
        // ignore malformed URL, fall through
      }
    }

    const own = Array.from(document.querySelectorAll<HTMLScriptElement>('script[src]')).find((s) =>
      /\/embed\.js(\?|$)/.test(s.src),
    )
    if (own) {
      try {
        return new URL(own.src).origin
      } catch {
        // ignore malformed URL, fall through
      }
    }
  }
  return typeof window !== 'undefined' ? window.location.origin : ''
}

let pending: Promise<Environment> | null = null

/** Fetches env.js (from this widget's own origin) and resolves once window.OnAim['minimal-widget'] is set. Call/await before any API call. */
export function loadEnv(): Promise<Environment> {
  if (pending) return pending

  pending = new Promise((resolve, reject) => {
    const existing = window.OnAim?.['minimal-widget']
    if (existing) {
      resolve(existing)
      return
    }

    const script = document.createElement('script')
    script.src = `${detectOrigin()}/env.js`
    script.onload = () => {
      const loaded = window.OnAim?.['minimal-widget']
      if (loaded) {
        resolve(loaded)
      } else {
        reject(new Error('[env] env.js loaded but did not set window.OnAim["minimal-widget"]'))
      }
    }
    script.onerror = () => reject(new Error(`[env] failed to load ${script.src}`))
    document.head.appendChild(script)
  })

  return pending
}

/** Read the already-loaded config. Throws if loadEnv() hasn't resolved yet. */
export function getEnv(): Environment {
  const env = window.OnAim?.['minimal-widget']
  if (!env) {
    throw new Error('[env] not loaded yet — await loadEnv() first')
  }
  return env
}
